import {
  FeatureName,
  IssueCreationSource,
  MediaType,
  PickerMode,
  useCurrentWorkspace,
  useIsFeatureEnabled,
} from '@gripp/shared-logic';
import {
  Button,
  Colors,
  NewIssue as NewIssueBase,
  Spacing,
  useSlideLayoutSafe,
} from '@gripp/shared-ui';
import { useCallback } from 'react';
import { Dimensions, Modal, StyleSheet, View } from 'react-native';
import { MediaPicker } from '../../shared/components/media/mediaPicker';
import { NewIssueModal as NewIssueModalOld } from './newIssueModal-old';

export type NewIssueModalProps = {
  assetId?: string;
  workspaceId?: string;
  isOpened: boolean;
  onComplete: () => void;
  onIssueCreated?: (issueId: string) => void;
  onCancel?: () => void;
  modalType: 'sheet' | 'modal';
  acceptedMediaTypes?: MediaType[];
  hideAdvancedSettings?: boolean;
  createdFrom?: IssueCreationSource;
  pickerMode?: 'picker' | 'camera';
  selectedLabels?: Array<{ id: string; name: string }>;
  onLabelsChange?: (labels: Array<{ id: string; name: string }>) => void;
};

export const NewIssueModal = (props: NewIssueModalProps) => {
  const isCropCircleEnabled = useIsFeatureEnabled(FeatureName.CropCircleRedesign);

  if (isCropCircleEnabled) {
    return <NewIssueModalBase {...props} />;
  }

  return <NewIssueModalOld {...props} />;
};

export const NewIssueModalBase = (props: NewIssueModalProps) => {
  const onComplete = () => {
    props.onComplete();
  };

  const onCancel = () => {
    if (!props.onCancel) {
      onComplete();
    } else {
      props.onCancel();
    }
  };

  return (
    <Modal
      presentationStyle={props.modalType === 'sheet' ? 'pageSheet' : undefined}
      transparent
      visible={props.isOpened}
      onRequestClose={props.onCancel}
    >
      <View style={getOuterContainerStyle(props.modalType)}>
        <NewIssueBody {...props} onComplete={onComplete} onCancel={onCancel} />
      </View>
    </Modal>
  );
};

export const NewIssueBody = (props: NewIssueModalProps) => {
  const currentWorkspace = useCurrentWorkspace();
  const slideLayout = useSlideLayoutSafe();
  const getWorkspaceId = useCallback(() => {
    return props.workspaceId ?? currentWorkspace?.id;
  }, [props.workspaceId, currentWorkspace]);

  return (
    <View style={getInnerContainerStyle(props.modalType)}>
      <NewIssueBase
        assetId={props.assetId as string}
        workspaceId={getWorkspaceId()}
        mediaPicker={MediaPicker}
        acceptedMediaTypes={props.acceptedMediaTypes}
        onIssueCreated={(id: string) => {
          props.onIssueCreated?.(id);
          props.onComplete();
        }}
        topBar={() => <></>}
        bottomBar={(barProps) => (
          <View style={styles.footer}>
            <Button
              style={styles.saveButton}
              onPress={!barProps.rightPressDisabled ? barProps.onSave : undefined}
              state={!barProps.rightPressDisabled ? 'active' : 'default'}
            >
              {barProps.saveLabel}
            </Button>
          </View>
        )}
        mode={PickerMode.PICKER}
        onCancel={props.onCancel}
        hideAdvancedSettings={props.hideAdvancedSettings}
        createdFrom={props.createdFrom}
        pickerMode={props.pickerMode}
        onSelectorEdit={slideLayout?.toggleThirdPanel}
        selectedLabels={props.selectedLabels}
        onLabelsChange={props.onLabelsChange}
      />
    </View>
  );
};

const getOuterContainerStyle = (modalType: string) => {
  return modalType === 'sheet' ? styles.sheetContainer : styles.modalContainer;
};

const getInnerContainerStyle = (modalType: string) => {
  return modalType === 'sheet'
    ? styles.sheetInnerContainer
    : styles.innerContainer;
};

const styles = StyleSheet.create({
  modalContainer: {
    flex: 1,
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingTop: Dimensions.get('window').height / 7,
    backgroundColor: Colors.white,
    zIndex: 1, // Ensure proper layering for dropdowns
  },
  innerContainer: {
    width: '100%',
    maxWidth: 560,
    height: 700, // Fixed height for proper scrolling
    borderRadius: 12,
    overflow: 'visible', // Allow dropdowns to render outside container
    zIndex: 1, // Ensure proper layering for dropdowns
  },
  sheetContainer: {
    flex: 1,
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  sheetInnerContainer: {
    width: '100%',
    height: '100%',
    maxHeight: '100%',
  },
  footer: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    paddingHorizontal: Spacing.baseMargin.base,
    paddingVertical: Spacing.baseMargin.base,
    backgroundColor: 'transparent',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  saveButton: {
    marginBottom: Spacing.baseMargin.base,
    borderRadius: 2,
    width: '100%',
    height: 48,
  },
});
